import { create } from 'zustand';
import {
  SIDE_PANEL_DEFAULT_SHARE,
  COLLAPSED_CHAT_DEFAULT_SHARE,
  type FlowPanelId,
} from './flow-panels-layout';
import { useShellFrameStore } from '../shell-frame-store';

/**
 * V2 Flow shell panel container — session-only open/closed/share state (plan
 * `2026-09-30-005-v2-panel-container-plan.md`, unit U2). This is the single
 * source of truth for the Chat/Actions open flags, the Flow-close intent, and
 * the three stored shares; `deriveFlowPanelsLayout` (`flow-panels-layout.ts`)
 * turns this plus a measured row width into what actually renders.
 *
 * ── Why zustand ───────────────────────────────────────────────────────────
 * CLAUDE.md's "Zustand for Module-Scope Client State" rule requires it for
 * any module-scope client store — a hand-rolled `useSyncExternalStore` or a
 * listener-`Set` bus is a lint violation (`terra/no-hand-rolled-external-store`)
 * and a new store never gets an `.oxlintrc.json` allowlist entry. Plain
 * `create<FlowPanelsState>(() => ({ ...initialState }))` is the documented
 * default form; the curried `create<T>()(...)` form is reserved for
 * middleware (e.g. `persist`), which this store deliberately does not use.
 *
 * ── Why session-only, no `persist` ───────────────────────────────────────
 * This is a product decision, not a technical one: panel open/closed state
 * and drag shares reset on reload like `EnrollQueue/panel-width-store.ts`
 * and `account/account-lens-layout-store.ts` — there is no `useLayoutPrefs`
 * (or equivalent) wiring for this surface, and none is being added here.
 *
 * ── Why writers are module-level functions, not store members ───────────
 * State is data-only so `useFlowPanelsStore.setState(initialState, true)`
 * (the CLAUDE.md test-reset contract — replace mode, not merge) can never
 * strip any writer behavior; the writers live outside the store object
 * entirely and keep working after a replace-mode reset.
 *
 * ── R4 invariant ─────────────────────────────────────────────────────────
 * **Panel writers (`openPanel`, `closePanel`, `togglePanel`) never touch
 * `flowClosed`.** Flow closes ONLY via `setFlowClosed`/`toggleFlowClosed` —
 * i.e. only via the Header's explicit Flow toggle — never as a side effect
 * of opening/closing a side panel, never from a drag, never from a viewport
 * change. `deriveFlowPanelsLayout` is what decides whether `flowClosed`
 * actually renders as "collapsed" vs. "empty state" vs. ignored; this store
 * never derives or clears it on its own.
 */

export interface FlowPanelsState {
  chatOpen: boolean;
  actionsOpen: boolean;
  /**
   * True while a route that opts out of the enroll queue (`manifest.shell.
   * hideEnrollQueue`: lead-scanner, value-calculator) is mounted inside the
   * frame. Authoritative: `openPanel('actions')`/`togglePanel('actions')` are
   * no-ops while set, so a `?panels=actions` deep link, a queue producer or
   * the URL READ effect can never reopen a column the app hid.
   */
  actionsSuppressed: boolean;
  flowClosed: boolean;
  chatShare: number;
  actionsShare: number;
  collapsedChatShare: number;
}

/** Exported per the CLAUDE.md zustand convention — tests reset with `setState(initialState, true)`. */
export const initialState: FlowPanelsState = {
  chatOpen: false,
  actionsOpen: false,
  actionsSuppressed: false,
  flowClosed: false,
  chatShare: SIDE_PANEL_DEFAULT_SHARE,
  actionsShare: SIDE_PANEL_DEFAULT_SHARE,
  collapsedChatShare: COLLAPSED_CHAT_DEFAULT_SHARE,
};

export const useFlowPanelsStore = create<FlowPanelsState>(() => ({ ...initialState }));

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Maps a panel id to its share key in `FlowPanelsState`. */
function shareKey(id: FlowPanelId): 'chatShare' | 'actionsShare' {
  return id === 'chat' ? 'chatShare' : 'actionsShare';
}

/** Open a side panel. R4: never touches `flowClosed`. */
export function openPanel(id: FlowPanelId): void {
  if (id === 'chat') {
    useFlowPanelsStore.setState({ chatOpen: true });
  } else if (!useFlowPanelsStore.getState().actionsSuppressed) {
    useFlowPanelsStore.setState({ actionsOpen: true });
  }
}

/** Close a side panel. R4: never touches `flowClosed`. */
export function closePanel(id: FlowPanelId): void {
  if (id === 'chat') {
    useFlowPanelsStore.setState({ chatOpen: false });
  } else {
    useFlowPanelsStore.setState({ actionsOpen: false });
  }
}

/** Toggle a side panel's open state. R4: never touches `flowClosed`. */
export function togglePanel(id: FlowPanelId): void {
  if (id === 'chat') {
    useFlowPanelsStore.setState((state) => ({ chatOpen: !state.chatOpen }));
  } else {
    useFlowPanelsStore.setState((state) => (state.actionsSuppressed ? state : { actionsOpen: !state.actionsOpen }));
  }
}

/**
 * Suppress (or release) the Actions panel for routes that hide the enroll
 * queue. Suppressing also closes an open column; releasing does NOT reopen it
 * (the old `closeEnrollQueue()` never restored either).
 */
export function setActionsSuppressed(suppressed: boolean): void {
  useFlowPanelsStore.setState(suppressed ? { actionsSuppressed: true, actionsOpen: false } : { actionsSuppressed: false });
}

/** Set Flow's close intent directly — the only writer allowed to touch `flowClosed` (R4). */
export function setFlowClosed(next: boolean): void {
  useFlowPanelsStore.setState({ flowClosed: next });
}

/** Flip Flow's close intent — the only other writer allowed to touch `flowClosed` (R4). */
export function toggleFlowClosed(): void {
  useFlowPanelsStore.setState((state) => ({ flowClosed: !state.flowClosed }));
}

/**
 * Commit a dragged side-panel share, clamped only to `[0, 1]` (D2) — floor
 * enforcement in pixels lives in the layout math and the drag handle, which
 * already clamp before calling this.
 */
export function setPanelShare(id: FlowPanelId, share: number): void {
  useFlowPanelsStore.setState({ [shareKey(id)]: clamp(share, 0, 1) });
}

/**
 * Commit BOTH side-panel shares in one write — the both-open drag path in
 * `FlowPanels`: the dragged panel's new share plus the other panel's current
 * EFFECTIVE share. Writing only the dragged panel would leave the other's
 * stale stored request (e.g. chat stretched to .75 while actions was closed)
 * to re-absorb every pixel the rep frees, and shrink the dragged handle's max
 * to match — a one-way ratchet. Same `[0, 1]` clamp as `setPanelShare`.
 */
export function setPanelShares(shares: { chat: number; actions: number }): void {
  useFlowPanelsStore.setState({
    chatShare: clamp(shares.chat, 0, 1),
    actionsShare: clamp(shares.actions, 0, 1),
  });
}

/** Reset a side panel's share back to `SIDE_PANEL_DEFAULT_SHARE`. */
export function resetPanelShare(id: FlowPanelId): void {
  useFlowPanelsStore.setState({ [shareKey(id)]: SIDE_PANEL_DEFAULT_SHARE });
}

/**
 * Commit a dragged collapsed-split share, clamped only to `[0, 1]` (D2) —
 * same rationale as `setPanelShare`.
 */
export function setCollapsedChatShare(share: number): void {
  useFlowPanelsStore.setState({ collapsedChatShare: clamp(share, 0, 1) });
}

/** Reset the collapsed Chat/Actions split back to `COLLAPSED_CHAT_DEFAULT_SHARE`. */
export function resetCollapsedChatShare(): void {
  useFlowPanelsStore.setState({ collapsedChatShare: COLLAPSED_CHAT_DEFAULT_SHARE });
}

/**
 * Whether the Chat panel is actually on screen: `chatOpen` alone is not enough,
 * because on an unframed route (Delphi) the chat surface is unmounted while the
 * store still says open. Read live (`getState()`), never as a dependency.
 */
export function isChatSurfaceVisible(): boolean {
  return useFlowPanelsStore.getState().chatOpen && useShellFrameStore.getState().frameActive;
}

/**
 * Bring the Chat panel into view from anywhere. The panel only renders inside
 * the frame, so an unframed route first navigates to a framed one.
 */
export function revealChat(navigate: (to: string) => void): void {
  if (!useShellFrameStore.getState().frameActive) navigate('/home');
  openPanel('chat');
}
